import React, { useContext, useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { CloseConversationDialog } from './CloseConversationDialog';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { useT } from '@/i18n/labels';
import { useAuthStore } from '@/stores/authStore';
import { useConversationStore } from '@/stores/conversationStore';
import { ConsoleContext } from '@/console/ConsoleContext';
import type { LearningTag } from '@/types';

interface CloseConversationActionProps {
  sessionId: string;
  /** Already closed: nothing to offer. */
  closed: boolean;
  /**
   * The viewer may not ACT on this conversation (no `workspace.handle`, a teammate owns it, the owner is unverified, or a
   * human holds it and nobody can be named): the button is not offered and a half-open dialog is dropped, so neither can be
   * confirmed on a conversation that is no longer this viewer's to act on. UX only - the server re-checks the close.
   */
  blocked: boolean;
}

/**
 * "Close conversation" - the full-width button at the BOTTOM of the contact card (operator 2026-11-02; it used to sit in
 * the chat header) and the confirm it opens: the SAME {@link CloseConversationDialog} with the "teach the bot from this
 * chat" choice. Confirming saves a CHANGED learning choice first (learning reads it when the conversation closes; if that
 * save fails nothing is closed and the dialog says so), then `POST /api/sessions/{id}/close`, marks the row closed and
 * re-reads it. A failed close re-reads the row too: a refusal ("Session not available") means the conversation became
 * somebody else's, and the console must learn that from the server rather than keep offering the act.
 *
 * Keep the ownership/permission gate exactly as the header button had it: `!closed && !blocked`.
 */
export const CloseConversationAction: React.FC<CloseConversationActionProps> = ({ sessionId, closed, blocked }) => {
  const { t } = useT();
  const agentId = useAuthStore((s) => s.user?.agentId);
  const markClosed = useConversationStore((s) => s.markClosed);
  // Optional on purpose: the card is also rendered where there is no shell (the row re-read is then simply skipped).
  const consoleCtx = useContext(ConsoleContext);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // No longer this viewer's to act on (or already closed): drop anything half-done.
  useEffect(() => {
    if (blocked || closed) {
      setOpen(false);
      setError(null);
    }
  }, [blocked, closed]);

  if (closed || blocked) return null;

  const openDialog = () => {
    setError(null);
    setFailed(false);
    setOpen(true);
  };

  const reread = () => {
    void consoleCtx?.refreshRow(sessionId);
  };

  const confirm = async (chosen: LearningTag, saved: LearningTag) => {
    if (busy) return;
    if (chosen !== saved) {
      setBusy(true);
      setError(null);
      try {
        await axiosClient.post(API.LEARNING.SET_TAG(sessionId), { tag: chosen });
      } catch {
        setBusy(false);
        setError(t('learningFailedToSave'));
        return;
      }
      setBusy(false);
    }
    setOpen(false);
    try {
      await axiosClient.post(API.SESSIONS_CLOSE(sessionId), {
        closedBy: agentId ?? 'agent',
        reason: 'agent_closed',
      });
      markClosed(sessionId);
      reread();
    } catch {
      reread();
      setFailed(true);
      window.setTimeout(() => setFailed(false), 4000);
    }
  };

  return (
    <div className="cp-close" data-testid="cp-close">
      <button
        type="button"
        className="ac-btn ghost sm cp-close-btn"
        onClick={openDialog}
        data-testid="close-btn"
        style={{ width: '100%' }}
      >
        <Icon name="x" size={15} />
        {t('closeConversation')}
      </button>

      {failed && (
        <div role="alert" data-testid="cp-close-error" className="cp-close-error">
          {t('actionFailedRetry')}
        </div>
      )}

      {open && (
        <CloseConversationDialog
          key={sessionId}
          sessionId={sessionId}
          busy={busy}
          error={error}
          onConfirm={confirm}
          onCancel={() => {
            if (busy) return;
            setOpen(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
};
